class ThwPredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;
    this.input = this.querySelector('[data-predictive-search-input]');
    this.form = this.querySelector('form[role="search"]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.status = this.querySelector('[data-predictive-search-status]');
    this.endpoint = this.dataset.predictiveUrl;
    this.abortController = new AbortController();

    if (!this.input || !this.form || !this.results || !this.status || !this.endpoint) return;

    const signal = this.abortController.signal;
    this.input.addEventListener('input', this.debounce(() => this.handleInput(), 250), { signal });
    this.input.addEventListener('keydown', (event) => this.handleInputKeydown(event), { signal });
    this.results.addEventListener('keydown', (event) => this.handleResultsKeydown(event), { signal });
    this.form.addEventListener('submit', () => this.close(), { signal });
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.close();
    }, { signal });
    document.addEventListener('focusin', (event) => {
      if (!this.contains(event.target)) this.close();
    }, { signal });
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    if (this.requestController) this.requestController.abort();
    this.initialised = false;
  }

  debounce(callback, wait) {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback.apply(this, args), wait);
    };
  }

  async handleInput() {
    const searchTerm = this.input.value.trim();
    if (searchTerm.length < 2) {
      if (this.requestController) this.requestController.abort();
      this.close(true);
      return;
    }

    if (this.requestController) this.requestController.abort();
    const requestController = new AbortController();
    this.requestController = requestController;
    this.setLoading(true);

    try {
      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'all');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.sku,body');

      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: requestController.signal
      });
      if (!response.ok) throw new Error('Predictive search request failed');

      const payload = await response.json();
      if (this.input.value.trim() !== searchTerm) return;
      const products = payload?.resources?.results?.products || [];
      this.renderResults(products, searchTerm);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.close(true);
      this.status.textContent = 'Search suggestions are unavailable. Submit the form to search all products.';
    } finally {
      if (this.requestController === requestController) this.setLoading(false);
    }
  }

  renderResults(products, searchTerm) {
    this.results.replaceChildren();

    const heading = document.createElement('p');
    heading.id = 'HeaderPredictiveProducts';
    heading.className = 'thw-predictive-search__heading';
    heading.textContent = products.length ? 'Suggested products' : 'No suggested products';
    this.results.append(heading);

    if (products.length) {
      const list = document.createElement('ul');
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-labelledby', heading.id);

      products.forEach((product) => {
        const item = document.createElement('li');
        item.setAttribute('role', 'option');

        const link = document.createElement('a');
        link.href = product.url;
        link.dataset.predictiveOption = '';

        const imageUrl = product.featured_image?.url || product.image;
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = product.featured_image?.alt || product.title;
          image.width = 72;
          image.height = 54;
          image.loading = 'lazy';
          link.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'thw-predictive-search__image-placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          link.append(placeholder);
        }

        const copy = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = product.title;
        copy.append(title);
        if (product.type) {
          const type = document.createElement('small');
          type.textContent = product.type;
          copy.append(type);
        }
        link.append(copy);
        item.append(link);
        list.append(item);
      });

      this.results.append(list);
    } else {
      const empty = document.createElement('p');
      empty.className = 'thw-predictive-search__empty';
      empty.textContent = 'Try another part name, vehicle or identifier.';
      this.results.append(empty);
    }

    const fullSearch = document.createElement('button');
    fullSearch.type = 'button';
    fullSearch.className = 'thw-predictive-search__full-search';
    fullSearch.dataset.predictiveOption = '';
    fullSearch.textContent = `Search all products for “${searchTerm}”`;
    fullSearch.addEventListener('click', () => this.form.requestSubmit());
    this.results.append(fullSearch);

    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.status.textContent = products.length
      ? `${products.length} product suggestion${products.length === 1 ? '' : 's'} available.`
      : 'No product suggestions available. Full search remains available.';
  }

  handleInputKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.form.requestSubmit();
      return;
    }
    if (event.key !== 'ArrowDown' || this.results.hidden) return;
    const firstOption = this.options()[0];
    if (!firstOption) return;
    event.preventDefault();
    firstOption.focus();
  }

  handleResultsKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.input.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const options = this.options();
    const currentIndex = options.indexOf(document.activeElement);
    if (currentIndex === -1) return;
    event.preventDefault();
    if (event.key === 'ArrowDown') options[(currentIndex + 1) % options.length].focus();
    if (event.key === 'ArrowUp' && currentIndex === 0) this.input.focus();
    else if (event.key === 'ArrowUp') options[currentIndex - 1].focus();
  }

  options() {
    return Array.from(this.results.querySelectorAll('[data-predictive-option]'));
  }

  setLoading(isLoading) {
    this.results.setAttribute('aria-busy', String(isLoading));
    if (isLoading) this.status.textContent = 'Loading product suggestions.';
  }

  close(clearResults = false) {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    if (clearResults) {
      this.results.replaceChildren();
      this.status.textContent = '';
    }
  }
}

if (!customElements.get('thw-predictive-search')) {
  customElements.define('thw-predictive-search', ThwPredictiveSearch);
}
